import {
  ArrowBack,
  AssignmentTurnedInOutlined,
  CheckCircleOutline,
  ExpandLess,
  ExpandMore,
  InfoOutlined,
  WarningRounded,
} from "@material-ui/icons";
import api from "api";
import { format, parseISO } from "date-fns";
import { observer } from "mobx-react-lite";
import GenerateCostSheetButton from "pages/DefineSection/GenerateCostSheetButton";
import { useProject } from "pages/Workspace/hooks";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Form, Spinner } from "react-bootstrap";
import Select from "react-select";
import { useStore } from "store";
import FormulationApproval from "store/models/FormulationApproval";
import styled from "styled-components";
import { Toast } from "utils";
import Button from "../Button";

const Information = styled(Alert)`
  align-items: flex-start;
  display: flex;
  font-size: 0.8125rem;
  line-height: 1.35rem;

  svg {
    flex: 0 0 auto;
    margin-right: 0.75rem;
  }
`;

const StyledSelect = styled(Select)`
  .react-select__option--is-selected {
    background-color: #c3da87;
    color: white;
  }

  .react-select__option--is-focused {
    background-color: #d4edda;
  }

  .react-select__multi-value {
    background-color: #c3da87;
  }

  .react-select__multi-value__label {
    color: black;
  }

  .react-select__multi-value__remove {
    color: black;

    &:hover {
      background-color: #c3da87;
      color: black;
    }
  }
`;

const Panel = styled.section`
  background: #ffffff;
  border: 1px solid #c8c8c8;
  margin: 0 1.25rem 1rem;
`;

const PanelHeader = styled.button`
  align-items: center;
  background: #ffffff;
  border: 0;
  display: flex;
  font-size: 1rem;
  font-weight: 600;
  min-height: 3.5rem;
  padding: 0 1rem;
  width: 100%;

  svg {
    margin-right: 0.75rem;
  }
`;

const PanelBody = styled.div`
  border-top: 1px solid #c8c8c8;
  padding: 1rem;
`;

const ApprovalFields = styled.div`
  max-width: 30.625rem;
`;

const CategoryRow = styled.div`
  margin-bottom: 2rem;
`;

const CategoryLabel = styled.label`
  color: #333333;
  display: block;
  font-size: 0.6875rem;
  font-weight: 700;
  margin-bottom: 0.625rem;
  text-transform: uppercase;
`;

const Footer = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  padding-top: 1rem;

  .spinner-border {
    margin-right: 0.625rem;
  }
`;

const ProgressBarContainer = styled.div`
  align-items: center;
  display: flex;
  margin-bottom: 1.25rem;
`;

const ProgressStep = styled.div<{ active: boolean; complete: boolean }>`
  align-items: center;
  color: ${({ active, complete }) =>
    active || complete ? "#74b469" : "#999999"};
  display: flex;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;

  span {
    align-items: center;
    border: 2px solid currentColor;
    border-radius: 50%;
    display: flex;
    flex: 0 0 auto;
    height: 1.5rem;
    justify-content: center;
    margin-right: 0.5rem;
    width: 1.5rem;
  }
`;

const ProgressConnector = styled.div<{ complete: boolean }>`
  border-top: 2px solid ${({ complete }) => (complete ? "#74b469" : "#c8c8c8")};
  flex: 1;
  margin: 0 0.75rem;
`;

const ApprovalStatus = styled.div`
  align-items: center;
  display: flex;
  font-size: 0.875rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-transform: uppercase;

  svg {
    color: #74b469;
    margin-right: 0.625rem;
  }
`;

const ApprovalMeta = styled.p`
  color: #666666;
  font-size: 0.75rem;
  margin-bottom: 1.25rem;
`;

const AssignmentSummary = styled.div`
  border-top: 1px solid #e5e5e5;
  display: grid;
  font-size: 0.8125rem;
  gap: 1rem;
  grid-template-columns: minmax(10rem, 1fr) minmax(16rem, 2fr);
  padding: 0.75rem 0;

  strong {
    font-size: 0.6875rem;
    text-transform: uppercase;
  }
`;

const AssignmentHeaderRow = styled(AssignmentSummary)`
  border-top: 0;
  color: #666666;
  font-size: 0.6875rem;
  font-weight: 700;
  padding-bottom: 0.375rem;
  text-transform: uppercase;
`;

const Comments = styled.section`
  border-top: 1px solid #c8c8c8;
  margin-top: 1rem;
  padding-top: 1rem;

  h4 {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
  }
`;

const CommentItem = styled.div`
  background: #f7f7f7;
  margin-bottom: 0.5rem;
  padding: 0.75rem;

  strong,
  time {
    font-size: 0.6875rem;
  }

  time {
    color: #666666;
    margin-left: 0.5rem;
  }

  p {
    font-size: 0.8125rem;
    margin: 0.375rem 0 0;
  }
`;

const CommentInput = styled(Form.Control)`
  border: 1px solid #c8c8c8;
  border-radius: 0.25rem;
  font-size: 0.8125rem;
  margin-top: 0.75rem;
  min-height: 5.5rem;
  padding: 0.75rem;
  resize: vertical;
  width: 100%;

  &:focus {
    border-color: #74b469;
    box-shadow: none;
  }
`;

const RequestComment = styled.div`
  margin-top: 0.5rem;

  label {
    color: #333333;
    display: block;
    font-size: 0.6875rem;
    font-weight: 700;
    margin-bottom: 0.625rem;
    text-transform: uppercase;
  }
`;

const RequiredAsterisk = styled.span`
  color: #d64545;
  margin-left: 0.25rem;
`;

const FieldError = styled.p`
  color: #d64545;
  font-size: 0.6875rem;
  margin: 0.375rem 0 0;
`;

type Option = { value: identifier; label: string };
type CategoryOption = { value: string; label: string; isMandatory: boolean };

const FORMULATION_APPROVAL_CATEGORIES: CategoryOption[] = [
  { value: "business_sales_marketing", label: "Business / Sales, Marketing", isMandatory: true },
  { value: "technology", label: "Technology", isMandatory: true },
  { value: "procurement", label: "Procurement", isMandatory: true },
  { value: "gsc_npi_operations", label: "GSC NPI / Operations", isMandatory: true },
  { value: "gsc_finance_pu_clt", label: "GSC Finance PU / CLT", isMandatory: true },
  { value: "pricing_global", label: "Pricing - Global Pricing Team only", isMandatory: false },
  { value: "admin", label: "Admin", isMandatory: true }, 
  { value: "adhoc", label: "Ad Hoc", isMandatory: false },
];

type Props = {
  onOpen: () => void;
};

const FormulationApprovalButton: React.FC<Props> = ({ onOpen }) => {
  const store = useStore();
  const project = useProject();
  const formulations = Array.from(store.formulations.listItems.values());
  const allFormulationsApproved =
    formulations.length > 0 &&
    formulations.every((formulation) => formulation.isApproved);

  return (
    <Button
      variant="clt-files"
      size="thin"
      onClick={onOpen}
      disabled={project?.loading || !allFormulationsApproved}
      title={
        allFormulationsApproved
          ? undefined
          : "Approve all formulation rows before sign off."
      }
    >
      <AssignmentTurnedInOutlined /> Sign Off
    </Button>
  );
};

const PROGRESS_STEPS: { step: 1 | 2; label: string }[] = [
  { step: 1, label: "Approvals" },
  { step: 2, label: "Approval Status" },
];

type ProgressBarProps = {
  step: 1 | 2;
};

const ProgressBar: React.FC<ProgressBarProps> = ({ step }) => (
  <ProgressBarContainer>
    {PROGRESS_STEPS.map((item, index) => (
      <React.Fragment key={item.step}>
        <ProgressStep active={step === item.step} complete={step > item.step}>
          <span>
            {step > item.step ? (
              <CheckCircleOutline fontSize="small" />
            ) : (
              item.step
            )}
          </span>
          {item.label}
        </ProgressStep>
        {index < PROGRESS_STEPS.length - 1 && (
          <ProgressConnector complete={step > item.step} />
        )}
      </React.Fragment>
    ))}
  </ProgressBarContainer>
);

type StepData = {
  categories: CategoryOption[];
  approverOptions: Option[];
  loadingChoices: boolean;
  approverIdsByCategory: Record<string, identifier[]>;
  onApproverChange: (categoryValue: string, approverIds: identifier[]) => void;
  comment: string;
  onCommentChange: (comment: string) => void;
  submitting: boolean;
  approval: FormulationApproval | undefined;
  onBack: () => void;
};

type FormulationApprovalStepProps = {
  step: 1 | 2;
  data: StepData;
  handleSubmit: () => void;
};

const isApprovedStatus = (approval?: FormulationApproval) =>
  approval?.status === "completed";

const isResubmitStatus = (approval?: FormulationApproval) =>
  approval?.status === "resubmit";

// Renders the "Request for Approval" form (step 1) or the "Requests Approved" summary (step 2).
const FormulationApprovalStep: React.FC<FormulationApprovalStepProps> = ({
  step,
  data,
  handleSubmit,
}) => {
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const handleApproverChange = (
    categoryValue: string,
    approverIds: identifier[]
  ) => {
    data.onApproverChange(categoryValue, approverIds);
    if (approverIds.length > 0) {
      setFieldErrors((current) => {
        const { [categoryValue]: _removed, ...rest } = current;
        return rest;
      });
    }
  };

  const handleSendForApproval = () => {
    const newErrors: Record<string, string> = {};
    data.categories.forEach((category) => {
      if (
        category.isMandatory &&
        !data.approverIdsByCategory[category.value]?.length
      ) {
        newErrors[category.value] = "This field is required.";
      }
    });
    setFieldErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      handleSubmit();
    }
  };

  if (
    step === 2 &&
    data.approval &&
    (isApprovedStatus(data.approval) || isResubmitStatus(data.approval))
  ) {
    const { approval } = data;
    return (
      <>
        <ApprovalStatus>
          {isResubmitStatus(approval) ? <WarningRounded /> : <CheckCircleOutline />}
          {isResubmitStatus(approval) ? "Re-Signoff Required" : "Approved"}
        </ApprovalStatus>
        <ApprovalMeta>
          Requested by {approval.requestedBy.name || approval.requestedBy.email} on{" "}
          {format(parseISO(approval.requestedAt), "d MMM yyyy, HH:mm")}
        </ApprovalMeta>
        <AssignmentHeaderRow>
          <span />
          <span>Approved Status</span>
        </AssignmentHeaderRow>
        {approval.categories.map((category) => (
          <AssignmentSummary key={category.code}>
            <strong>{category.name}</strong>
            <span>
              {category.approvers
                .map((approver) => approver.name || approver.email)
                .join(", ")}
            </span>
          </AssignmentSummary>
        ))}
        <Comments>
          <h4>Comments</h4>
          {approval.comments.map((item) => (
            <CommentItem key={item.id}>
              <strong>{item.user.name || item.user.email}</strong>
              <time>{format(parseISO(item.postedAt), "d MMM yyyy, HH:mm")}</time>
              <p>{item.comment}</p>
            </CommentItem>
          ))}
        </Comments>
        <Footer>
          <Button variant="borderless" onClick={data.onBack} disabled={data.submitting}>
            <ArrowBack /> {isResubmitStatus(approval) ? "Re-Signoff" : "Back"}
          </Button>
          <GenerateCostSheetButton disabled={isResubmitStatus(approval)} />
        </Footer>
      </>
    );
  }

  return (
    <>
      <ApprovalFields>
        {data.categories.map((category) => (
          <CategoryRow key={category.value}>
            <CategoryLabel>
              {category.label}
              {category.isMandatory && <RequiredAsterisk>*</RequiredAsterisk>}
            </CategoryLabel>
            <StyledSelect
              classNamePrefix="react-select"
              isMulti
              options={data.approverOptions}
              isLoading={data.loadingChoices}
              value={data.approverOptions.filter((option) =>
                data.approverIdsByCategory[category.value]?.includes(option.value)
              )}
              onChange={(options: any) =>
                handleApproverChange(
                  category.value,
                  (options as readonly Option[]).map((option) => option.value)
                )
              }
              noOptionsMessage={() => "No active users found"}
              placeholder="Select approvers"
            />
            {fieldErrors[category.value] && (
              <FieldError>{fieldErrors[category.value]}</FieldError>
            )}
          </CategoryRow>
        ))}
        <RequestComment>
          <label htmlFor="approval-request-comment">Comments</label>
          <CommentInput
            id="approval-request-comment"
            as="textarea"
            rows={4}
            maxLength={200}
            value={data.comment}
            onChange={(event) => data.onCommentChange(event.target.value)}
            placeholder="Add a comment for the approvers"
          />
        </RequestComment>
      </ApprovalFields>
      <Footer>
        <Button
          onClick={handleSendForApproval}
          disabled={data.submitting || data.loadingChoices}
        >
          {data.submitting ? (
            <>
              <Spinner animation="border" size="sm" /> Approving
            </>
          ) : (
            <>
              <CheckCircleOutline /> Approve
            </>
          )}
        </Button>
      </Footer>
    </>
  );
};

type PanelProps = {
  expanded: boolean;
  onToggle: () => void;
  onApprovalChange?: (approved: boolean) => void;
};

export const FormulationApprovalPanel: React.FC<PanelProps> = observer(
  ({ expanded, onToggle, onApprovalChange }) => {
  const project = useProject();
  const store = useStore();
  const currentUser = store.auth.current;
  const approval = project
    ? store.formulationApprovals.ofProject(project.id)
    : undefined;
  const [viewStep, setViewStep] = useState<1 | 2 | null>(null);
  const [approverIdsByCategory, setApproverIdsByCategory] = useState<
    Record<string, identifier[]>
  >({});
  const [approverOptions, setApproverOptions] = useState<Option[]>([]);
  const [loadingChoices, setLoadingChoices] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (!expanded || approverOptions.length > 0) return;

    setLoadingChoices(true);
    api.users
      .list(undefined, undefined, { is_active: true })
      .then((response) =>
        setApproverOptions(
          response.results.map((user) => ({
            value: user.id,
            label: user.name || user.email,
          }))
        )
      )
      .catch(() => Toast.danger("Approvers could not be loaded."))
      .finally(() => setLoadingChoices(false));
  }, [approverOptions.length, expanded]);

  useEffect(() => {
    if (!project) return;

    store.formulationApprovals
      .list({ project: project.id }, undefined, undefined)
      .then(({ results }) => {
        const existing = results;

        // No sign off yet, or one still awaiting approval: collect approvals in step 1.
        if (!existing || existing.status === "pending") {
          setViewStep(1);
          return;
        }

        setApproverIdsByCategory((current) => {
          const next = { ...current };
          existing.categories.forEach((category) => {
            if (!next[category.code]?.length) {
              next[category.code] = category.approvers.map(
                (approver) => approver.id
              );
            }
          });
          return next;
        });

        const [requestComment] = existing.comments;
        if (requestComment) {
          setComment(requestComment.comment);
        }

        setViewStep(2);
      })
      .catch(() => {
        setViewStep(1);
        Toast.danger("Sign off details could not be loaded.");
      });
  }, [project, store.formulationApprovals]);

  // Saved approvers who are no longer active users must not stay selected.
  const selectableApproverIds = useMemo(() => {
    const optionIds = new Set(approverOptions.map((option) => option.value));
    return Object.fromEntries(
      Object.entries(approverIdsByCategory).map(([code, ids]) => [
        code,
        ids.filter((id) => optionIds.has(id)),
      ])
    ) as Record<string, identifier[]>;
  }, [approverIdsByCategory, approverOptions]);

  const submit = useCallback(async () => {
    if (!project || !currentUser) return;

    setSubmitting(true);
    try {
      await store.formulationApprovals.create(project.id, {
        categories: FORMULATION_APPROVAL_CATEGORIES.map((category) => ({
          code: category.value,
          approver_ids: selectableApproverIds[category.value] ?? [],
        })),
        comment: comment.trim() || undefined,
      });
      setViewStep(2);
      Toast.success("Formulation approval completed.");
    } catch (e) {
      Toast.danger("Formulation approval could not be completed.");
    } finally {
      setSubmitting(false);
    }
  }, [
    comment,
    currentUser,
    project,
    selectableApproverIds,
    store.formulationApprovals,
  ]);

  useEffect(() => {
    onApprovalChange?.(isApprovedStatus(approval));
  }, [approval, onApprovalChange]);

  const handleBack = useCallback(() => {
    if (isResubmitStatus(approval)) {
      setApproverIdsByCategory({});
      setComment("");
    }
    setViewStep(1);
  }, [approval]);

  const stepData: StepData = {
    categories: FORMULATION_APPROVAL_CATEGORIES,
    approverOptions,
    loadingChoices,
    approverIdsByCategory: selectableApproverIds,
    onApproverChange: (categoryValue, approverIds) =>
      setApproverIdsByCategory((current) => ({
        ...current,
        [categoryValue]: approverIds,
      })),
    comment,
    onCommentChange: setComment,
    submitting,
    approval,
    onBack: handleBack,
  };
  const showResubmitInformation =
    viewStep === 2 && isResubmitStatus(approval);

  return (
    <Panel>
      <PanelHeader type="button" onClick={onToggle}>
        {expanded ? <ExpandLess /> : <ExpandMore />}
        Sign Off
      </PanelHeader>
      {expanded && viewStep !== null && (
        <PanelBody>
          <ProgressBar step={viewStep} />
          <Information variant="info">
            <InfoOutlined />
            <span>
              {showResubmitInformation
                ? "Admin has made some changes. Please perform sign off process again."
                : "Select an approval category and one or more approvers. Changes made after approval require another sign off."}
            </span>
          </Information>
          <FormulationApprovalStep
            step={viewStep}
            data={stepData}
            handleSubmit={submit}
          />
        </PanelBody>
      )}
    </Panel>
  );
  }
);

export default observer(FormulationApprovalButton);